import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import { GradientContainer, Logo } from '../../components';
import { Title, Description, ErrorMessage } from '../../components/typography';
import { Button } from '../../components/buttons';
import * as LicenseAPI from '../../api/Licenses';
import { useAuthenticatedUser } from '../../hooks/user';
import type { LicenseStatus } from '../../api/Licenses';

function Checkout() {
  const navigate = useNavigate();
  const user = useAuthenticatedUser();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  useEffect(() => {
    // Redirect if not a teacher
    if (user && user.role !== 'teacher') {
      navigate('/gallery');
      return;
    }

    // Fetch license status
    if (user && user.role === 'teacher') {
      setIsLoadingStatus(true);
      LicenseAPI.getLicenseStatus()
        .then((response) => {
          setLicenseStatus(response.data.data);
        })
        .catch(() => {
          // Error fetching license status - will show subscribe option
          setLicenseStatus(null);
        })
        .finally(() => {
          setIsLoadingStatus(false);
        });
    }
  }, [user, navigate]);

  const handleCheckout = async () => {
    if (!user || user.role !== 'teacher') {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await LicenseAPI.createCheckoutSession();
      if (response.data.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.url;
      } else {
        setError('Failed to create checkout session');
        setLoading(false);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          'Failed to start checkout. Please try again.',
      );
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user || user.role !== 'teacher') {
      return;
    }

    setPortalLoading(true);
    setError(null);

    try {
      const returnUrl = `${window.location.origin}/billing/checkout`;
      const response = await LicenseAPI.createPortalSession(returnUrl);
      if (response.data.data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = response.data.data.url;
      } else {
        setError('Failed to create portal session');
        setPortalLoading(false);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          'Failed to open subscription management. Please try again.',
      );
      setPortalLoading(false);
    }
  };

  if (!user || user.role !== 'teacher') {
    return null;
  }

  const hasActiveLicense = licenseStatus?.has_active_license ?? false;
  const hasSubscriptions = licenseStatus?.subscriptions && licenseStatus.subscriptions.length > 0;

  return (
    <GradientContainer mx="40px">
      <Grid container direction="column" alignItems="center" spacing={0}>
        <Grid item>
          <Logo />
        </Grid>

        <Grid
          item
          sx={{
            textAlign: 'center',
            marginY: 5,
          }}
        >
          <Title>
            {hasActiveLicense ? 'Subscription Active' : 'Get Started with Pressto'}
          </Title>
          <Description style={{ textAlign: 'center', maxWidth: '500px' }}>
            {hasActiveLicense
              ? 'Your subscription is active. Manage your subscription, update payment methods, or view billing history.'
              : 'Subscribe to access all teacher features and create unlimited assignments.'}
          </Description>
        </Grid>

        <Grid item>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            {isLoadingStatus ? (
              <Description>Loading subscription status...</Description>
            ) : hasActiveLicense ? (
              <>
                {hasSubscriptions && (
                  <Description style={{ textAlign: 'center', maxWidth: '500px', marginBottom: '1rem' }}>
                    {licenseStatus.subscriptions[0].cancel_at_period_end
                      ? 'Your subscription will cancel at the end of the current billing period.'
                      : 'Your subscription is active and will renew automatically.'}
                  </Description>
                )}
                <div style={{ maxWidth: '220px' }}>
                  <Button
                    onClick={handleManageSubscription}
                    label={portalLoading ? 'Loading...' : 'Manage Subscription'}
                    disabled={portalLoading}
                    large
                  />
                </div>
              </>
            ) : (
              <>
                <div style={{ maxWidth: '220px' }}>
                  <Button
                    onClick={handleCheckout}
                    label={loading ? 'Loading...' : 'Subscribe Now'}
                    disabled={loading}
                    large
                  />
                </div>
                <Description style={{ textAlign: 'center', maxWidth: '500px' }}>
                  Your subscription will renew monthly. You can cancel anytime and
                  maintain access through the end of your billing period.
                </Description>
              </>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}
          </div>
        </Grid>
      </Grid>
    </GradientContainer>
  );
}

export default Checkout;
